/**
 * useUsers - Hooks for user management (admin only).
 *
 * Provides query and mutation hooks for managing users via the backend API.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listUsers, createUser, updateUser, deleteUser, type UpdateUserRequest } from '@/lib/api';

/**
 * Query keys for user management.
 */
export const userQueryKeys = {
  all: ['users'] as const,
  lists: () => [...userQueryKeys.all, 'list'] as const,
  list: () => [...userQueryKeys.lists()] as const,
};

/**
 * Hook for fetching the list of all users.
 */
export function useUsers() {
  return useQuery({
    queryKey: userQueryKeys.list(),
    queryFn: listUsers,
  });
}

/**
 * Hook for creating a new user.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      // Invalidate users list to refetch
      queryClient.invalidateQueries({ queryKey: userQueryKeys.list() });
    },
  });
}

/**
 * Hook for updating a user.
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserRequest }) => updateUser(userId, data),
    onSuccess: () => {
      // Invalidate users list to refetch
      queryClient.invalidateQueries({ queryKey: userQueryKeys.list() });
    },
  });
}

/**
 * Hook for deleting a user.
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      // Invalidate users list to refetch
      queryClient.invalidateQueries({ queryKey: userQueryKeys.list() });
    },
  });
}
